import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  accessManagerApi,
  type CreateMemberInput,
  type UpdateMemberInput,
  type CreateRoleInput,
  type UpdateRoleInput,
  type CreateGrantInput,
  type UpdateGrantInput,
} from '@/lib/api/accessManager';

// ── Query keys ──────────────────────────────────────────────────────────

const KEYS = {
  members: (companyId?: number, appType?: string) =>
    ['access-manager', 'members', { companyId, appType }] as const,
  member: (id: number) => ['access-manager', 'member', id] as const,
  roles: (companyId?: number, appType?: string) =>
    ['access-manager', 'roles', { companyId, appType }] as const,
  role: (id: number) => ['access-manager', 'role', id] as const,
  grants: (direction: 'outgoing' | 'incoming', appType?: string) =>
    ['access-manager', 'grants', { direction, appType }] as const,
  companies: ['access-manager', 'companies'] as const,
  effective: (userId: number, appType?: string) =>
    ['access-manager', 'effective', userId, { appType }] as const,
};

// ── Members ─────────────────────────────────────────────────────────────

export function useMembers(params: { companyId?: number; appType?: string } = {}) {
  return useQuery({
    queryKey: KEYS.members(params.companyId, params.appType),
    queryFn: () => accessManagerApi.listMembers(params),
  });
}

export function useCreateMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateMemberInput) => accessManagerApi.createMember(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['access-manager', 'members'] }),
  });
}

export function useUpdateMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: UpdateMemberInput }) =>
      accessManagerApi.updateMember(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['access-manager', 'members'] }),
  });
}

export function useDeleteMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => accessManagerApi.deleteMember(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['access-manager', 'members'] }),
  });
}

export function useSetMemberPassword() {
  // No cache invalidation needed — the password isn't stored in any query.
  return useMutation({
    mutationFn: ({ id, newPassword }: { id: number; newPassword: string }) =>
      accessManagerApi.setMemberPassword(id, newPassword),
  });
}

export function useEffectivePermissions(userId: number | null, appType?: string) {
  return useQuery({
    queryKey: KEYS.effective(userId ?? 0, appType),
    queryFn: () => accessManagerApi.getEffectivePermissions(userId!, appType),
    enabled: !!userId,
  });
}

// ── Roles ───────────────────────────────────────────────────────────────

export function useRoles(params: { companyId?: number; appType?: string } = {}) {
  return useQuery({
    queryKey: KEYS.roles(params.companyId, params.appType),
    queryFn: () => accessManagerApi.listRoles(params),
  });
}

/**
 * Role/permission/grant mutations change what the user can see across the
 * whole app, so we nuke ALL queries to force a refetch. This is heavy but
 * matches the user's expectation that "I edit the role and immediately see
 * the new scope reflected everywhere".
 */
function invalidateAllAccessAware(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['access-manager'] });
  // Brand portfolio + company-scoped lists shown on home/insights/stock.
  qc.invalidateQueries(); // global — staleTime=30s would otherwise mask the change
}

export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateRoleInput) => accessManagerApi.createRole(body),
    onSuccess: () => invalidateAllAccessAware(qc),
  });
}

export function useUpdateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: UpdateRoleInput }) =>
      accessManagerApi.updateRole(id, body),
    onSuccess: () => invalidateAllAccessAware(qc),
  });
}

export function useDeleteRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => accessManagerApi.deleteRole(id),
    onSuccess: () => invalidateAllAccessAware(qc),
  });
}

// ── Grants ──────────────────────────────────────────────────────────────

export function useGrants(direction: 'outgoing' | 'incoming' = 'outgoing', appType?: string) {
  return useQuery({
    queryKey: KEYS.grants(direction, appType),
    queryFn: () => accessManagerApi.listGrants(direction, appType),
  });
}

export function useCreateGrant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateGrantInput) => accessManagerApi.createGrant(body),
    onSuccess: () => invalidateAllAccessAware(qc),
  });
}

export function useUpdateGrant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: UpdateGrantInput }) =>
      accessManagerApi.updateGrant(id, body),
    onSuccess: () => invalidateAllAccessAware(qc),
  });
}

export function useRevokeGrant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => accessManagerApi.revokeGrant(id),
    onSuccess: () => invalidateAllAccessAware(qc),
  });
}

// ── Manageable companies ───────────────────────────────────────────────

export function useManageableCompanies() {
  return useQuery({
    queryKey: KEYS.companies,
    queryFn: () => accessManagerApi.listManageableCompanies(),
  });
}
